import { useQuery } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { AppUiProviders } from "~/components/app-providers";
import { LazyPrivyAppProvider } from "~/components/lazy-privy-app-provider";
import {
  GuestAppAuthProvider,
  SiweAuthProvider,
  SyntheticDevAuthProvider,
  useAppAuth,
} from "~/lib/auth/app-auth";
import { CurrentUserIdentityController } from "~/lib/auth/canonical-user-identity";
import {
  useSiweAuthProfile,
  useSyntheticDevAuthProfile,
  type SiweAuthProfile,
  type SyntheticDevAuthProfile,
} from "~/lib/auth/app-auth-runtime";
import { useVmPreference } from "~/lib/auth/vm-preference";
import { isExecutionStreamBrowserE2EMode } from "~/lib/e2e/is-execution-stream-browser-e2e-mode";
import { isMarketDataStreamBrowserE2EMode } from "~/lib/e2e/is-market-data-stream-browser-e2e-mode";
import { isWalletlessBrowserE2EMode } from "~/lib/e2e/is-walletless-browser-e2e-mode";
import { getPrivyAppId } from "~/lib/env";
import { getUserFeatureFlagsQueryOptions } from "~/lib/feature-flags";
import { LocaleBoundary } from "~/lib/language-lens";
import { useAppSessionScope } from "~/lib/app-session-scope";
import { queryClient } from "~/lib/query-client";
import { initializeBrowserQueryPersistence } from "~/lib/query-persistence";
import { createRouterRuntimeContext } from "~/lib/router-runtime";
import { schedulePostPaint } from "~/lib/schedule-post-paint";
import { TelemetryRuntimeController } from "~/lib/telemetry/runtime-controller";
import { getAppAuthTransportAuthHeadersForSnapshot } from "~/lib/auth/app-auth-runtime";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import { createBrowserPlatformApiTrpcAdapter } from "~/lib/trpc/platform-api-adapter";
import {
  createUiConfigTrpcSyncClient,
  quarantineUiConfigOverlayForPrincipal,
  useUiConfigServerSync,
} from "~/lib/ui-config";
import { router } from "~/router";

export type AppProviderMode = "guest" | "privy" | "siwe" | "synthetic";

const BROWSER_QUERY_PERSISTENCE_FALLBACK_DELAY_MS = 250;
const QUERY_PERSISTENCE_READY_EXACT_PATHS = [
  "/",
  "/api",
  "/developers",
  "/platform",
  "/policy",
  "/terms",
] as const;

export interface ResolveAppProviderModeInput {
  hasPrivyConfig: boolean;
  isExecutionStreamBrowserSystemMode: boolean;
  isMarketDataStreamBrowserSystemMode: boolean;
  isWalletlessBrowserSystemMode: boolean;
  pathname: string;
  siweProfile: SiweAuthProfile | null;
  syntheticProfile: SyntheticDevAuthProfile | null;
}

export interface ResolvedAppProvidersProps extends Partial<ResolveAppProviderModeInput> {}

function isQueryPersistenceReadyBootstrapPathname(pathname: string): boolean {
  return QUERY_PERSISTENCE_READY_EXACT_PATHS.includes(
    pathname as (typeof QUERY_PERSISTENCE_READY_EXACT_PATHS)[number],
  );
}

export function resolveAppProviderMode(
  input: ResolveAppProviderModeInput,
): AppProviderMode {
  if (
    input.isWalletlessBrowserSystemMode ||
    input.isExecutionStreamBrowserSystemMode ||
    input.isMarketDataStreamBrowserSystemMode ||
    input.pathname === "/design-system" ||
    // Public shared receipts (/r/:slug) are anonymous by design: their data
    // path is the public rate-limited endpoint, so don't boot the auth SDK
    // for link visitors.
    input.pathname.startsWith("/r/")
  ) {
    return "guest";
  }

  if (input.siweProfile) {
    return "siwe";
  }

  if (
    input.syntheticProfile &&
    (import.meta.env.DEV || import.meta.env.MODE === "test")
  ) {
    return "synthetic";
  }

  if (input.hasPrivyConfig) {
    return "privy";
  }

  return "guest";
}

export function AppRouterRuntimeProvider() {
  const { authenticated, ready, transport, user } = useAppAuth();
  const { isLoaded, preference } = useVmPreference();
  const pathname = useRouterPathname();
  const [, setQueryPersistenceReady] = useState(() =>
    isQueryPersistenceReadyBootstrapPathname(pathname),
  );
  const platformApi = useMemo(
    () =>
      createBrowserPlatformApiTrpcAdapter({
        authRuntime: {
          getAuthHeaders: async () =>
            await getAppAuthTransportAuthHeadersForSnapshot(transport),
          getSnapshot: () => transport,
        },
        batch: true,
        getVmPreference: () => preference,
      }),
    [
      preference,
      transport.devAuthToken,
      transport.evmAddress,
      transport.mode,
      transport.siweAccessToken,
      transport.solanaAddress,
      transport.userId,
      transport.walletAddress,
    ],
  );
  const context = useMemo(
    () =>
      createRouterRuntimeContext({
        authenticated,
        platformApi,
        queryClient,
        ready,
        user,
        vmPreference: preference,
        vmPreferenceLoaded: isLoaded,
      }),
    [authenticated, isLoaded, platformApi, preference, ready, user],
  );
  const sessionScope = useAppSessionScope();
  const uiConfigPlatformApi = useMemo(() => getBrowserPlatformApiClient(), []);
  const { data: uiConfigFeatureFlags } = useQuery({
    ...getUserFeatureFlagsQueryOptions({
      authQueryScope: sessionScope.authScope,
      platformApi: uiConfigPlatformApi,
    }),
    enabled: ready && isLoaded && authenticated,
  });
  const uiConfigSyncClient = useMemo(
    () => createUiConfigTrpcSyncClient(uiConfigPlatformApi),
    [uiConfigPlatformApi],
  );
  const authUserId = user?.id ?? null;

  // This controller must stay outside LocaleBoundary: remounting it can cancel
  // a pending first preference push and let the replacement adopt stale remote
  // state before that explicit choice reaches the server.
  useLayoutEffect(() => {
    if (authUserId !== null) {
      quarantineUiConfigOverlayForPrincipal(authUserId);
    }
  }, [authUserId]);
  useUiConfigServerSync({
    client: uiConfigSyncClient,
    enabled:
      authenticated &&
      uiConfigFeatureFlags?.flags["addon_platform_ui_config"] === true,
    // Keyed by USER id, never a wallet address — wallets vary across VMs
    // for the same user; config identity must not.
    principalKey: authUserId,
  });

  return (
    <>
      <BrowserQueryPersistenceController
        onReadyChange={setQueryPersistenceReady}
        pathname={pathname}
      />
      <CurrentUserIdentityController context={context} />
      <TelemetryRuntimeController context={context} pathname={pathname} />
      <LocaleBoundary>
        <RouterProvider context={context} router={router} />
      </LocaleBoundary>
    </>
  );
}

function BrowserQueryPersistenceController(props: {
  onReadyChange: (ready: boolean) => void;
  pathname: string;
}) {
  const hasInitializedRef = useRef(false);
  const { onReadyChange, pathname } = props;

  useEffect(() => {
    if (isQueryPersistenceReadyBootstrapPathname(pathname)) {
      onReadyChange(true);
      return;
    }

    if (hasInitializedRef.current) {
      onReadyChange(true);
      return;
    }

    onReadyChange(false);
    let cancelled = false;
    const cancelSchedule = schedulePostPaint(() => {
      if (hasInitializedRef.current) {
        return;
      }

      hasInitializedRef.current = true;
      void initializeBrowserQueryPersistence(queryClient).finally(() => {
        if (!cancelled) {
          onReadyChange(true);
        }
      });
    }, BROWSER_QUERY_PERSISTENCE_FALLBACK_DELAY_MS);

    return () => {
      cancelled = true;
      cancelSchedule();
    };
  }, [onReadyChange, pathname]);

  return null;
}

function subscribeToRouterPathname(onStoreChange: () => void): () => void {
  return router.subscribe("onResolved", () => {
    onStoreChange();
  });
}

function getRouterPathnameSnapshot(): string {
  return router.state.location.pathname;
}

function useRouterPathname(): string {
  return useSyncExternalStore(
    subscribeToRouterPathname,
    getRouterPathnameSnapshot,
    () => "/",
  );
}

function useResolvedProviderPathname(
  explicitPathname: string | undefined,
): string {
  const routerPathname = useRouterPathname();

  return explicitPathname ?? routerPathname;
}

export function ResolvedAppProviders(props: ResolvedAppProvidersProps = {}) {
  const runtimeSyntheticProfile = useSyntheticDevAuthProfile();
  const runtimeSiweProfile = useSiweAuthProfile();
  const siweProfile = props.siweProfile ?? runtimeSiweProfile;
  const syntheticProfile = props.syntheticProfile ?? runtimeSyntheticProfile;
  const pathname = useResolvedProviderPathname(props.pathname);
  const mode = resolveAppProviderMode({
    hasPrivyConfig: props.hasPrivyConfig ?? getPrivyAppId() !== null,
    isExecutionStreamBrowserSystemMode:
      props.isExecutionStreamBrowserSystemMode ??
      isExecutionStreamBrowserE2EMode(),
    isMarketDataStreamBrowserSystemMode:
      props.isMarketDataStreamBrowserSystemMode ??
      isMarketDataStreamBrowserE2EMode(),
    isWalletlessBrowserSystemMode:
      props.isWalletlessBrowserSystemMode ?? isWalletlessBrowserE2EMode(),
    pathname,
    siweProfile,
    syntheticProfile,
  });

  switch (mode) {
    case "siwe":
      return (
        <SiweAuthProvider>
          <AppUiProviders>
            <AppRouterRuntimeProvider />
          </AppUiProviders>
        </SiweAuthProvider>
      );
    case "synthetic":
      return (
        <SyntheticDevAuthProvider>
          <AppUiProviders>
            <AppRouterRuntimeProvider />
          </AppUiProviders>
        </SyntheticDevAuthProvider>
      );
    case "privy":
      return (
        <LazyPrivyAppProvider pathname={pathname}>
          <AppUiProviders>
            <AppRouterRuntimeProvider />
          </AppUiProviders>
        </LazyPrivyAppProvider>
      );
    default:
      return (
        <GuestAppAuthProvider>
          <AppUiProviders>
            <AppRouterRuntimeProvider />
          </AppUiProviders>
        </GuestAppAuthProvider>
      );
  }
}
